import {
  Activity,
  ArrowLeft,
  BarChart3,
  Bell,
  BookOpen,
  Calendar,
  ChevronDown,
  ChevronUp,
  DatabaseZap,
  FileText,
  Image,
  Loader2,
  Pencil,
  Plus,
  Send,
  ShieldCheck,
  Trash2,
  TrendingUp,
  Upload,
  Users,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'

interface AdminScreenProps {
  onBack: () => void
  userEmail: string
}

interface BannerItem {
  screen: string
  isActive: boolean
  message: string
  bannerType: 'warning' | 'info'
}
const BANNER_SCREENS: { key: string; label: string }[] = [
  { key: 'global', label: 'Global (all screens)' },
  { key: 'home', label: 'Home' },
  { key: 'askreinz', label: 'AskREINZ' },
  { key: 'insights', label: 'Market Insights' },
  { key: 'estimate', label: 'Estimate' },
  { key: 'events', label: 'Events' },
  { key: 'education', label: 'Education' },
  { key: 'memberperks', label: 'Member Perks' },
  { key: 'myprofile', label: 'My Profile' },
]

interface Stats {
  responsesTotal: number
  responsesToday: number
  activeQuestions: number
  totalAdmins: number
  uniqueRespondents: number
}

interface SurveyOption {
  id: number
  optionValue: string
  optionLabel: string
  displayOrder: number
}

interface SurveyQuestion {
  id: number
  question: string
  isActive: boolean
  activeDate: string | null
  createdAt: string
  responseCount: number
  options: SurveyOption[]
}

interface AdminUser {
  id: number
  email: string
  name: string
  isActive: boolean
  createdAt: string
}

export function AdminScreen({ onBack, userEmail }: AdminScreenProps) {
  const { toast } = useToast()

  // Stats
  const [stats, setStats] = useState<Stats | null>(null)
  const [statsLoading, setStatsLoading] = useState(true)

  // Cache purge
  const [cachePurging, setCachePurging] = useState(false)

  // Survey
  const [questions, setQuestions] = useState<SurveyQuestion[]>([])
  const [questionsLoading, setQuestionsLoading] = useState(false)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [showAddQuestion, setShowAddQuestion] = useState(false)
  const [newQuestion, setNewQuestion] = useState('')
  const [newActiveDate, setNewActiveDate] = useState('')
  const [newOptions, setNewOptions] = useState([
    { value: '', label: '' },
    { value: '', label: '' },
  ])
  const [saving, setSaving] = useState(false)

  // Team
  const [admins, setAdmins] = useState<AdminUser[]>([])
  const [teamLoading, setTeamLoading] = useState(false)
  const [newAdminEmail, setNewAdminEmail] = useState('')
  const [newAdminName, setNewAdminName] = useState('')
  const [addingAdmin, setAddingAdmin] = useState(false)

  // Notifications compose
  const [notifTitle, setNotifTitle] = useState('')
  const [notifBody, setNotifBody] = useState('')
  const [notifUrl, setNotifUrl] = useState('')
  const [notifTarget, setNotifTarget] = useState<'all' | 'region' | 'organisation'>('all')
  const [notifTargetValue, setNotifTargetValue] = useState('')
  const [notifRegions, setNotifRegions] = useState<string[]>([])
  const [notifOrgs, setNotifOrgs] = useState<string[]>([])
  const [notifSending, setNotifSending] = useState(false)
  const [recentNotifs, setRecentNotifs] = useState<
    {
      id: number
      type: string
      title: string
      body?: string
      url?: string
      source: string
      publishedAt: string
    }[]
  >([])
  const [recentNotifsLoading, setRecentNotifsLoading] = useState(false)

  // Benefits
  interface BenefitItem {
    id: number
    name: string
    logoUrl: string | null
    headline: string
    description: string | null
    redemptionType: string
    voucherCode: string | null
    flyerUrl: string | null
    websiteUrl: string | null
    howToSteps: string | null
    attachmentsJson: string | null
    barcodeUrl: string | null
    isActive: boolean
    sortOrder: number
  }
  const REDEMPTION_TYPES = ['Flyer', 'MemberID', 'StaticCode', 'LinkOnly']
  const [benefits, setBenefits] = useState<BenefitItem[]>([])
  const [benefitsLoading, setBenefitsLoading] = useState(false)
  const [showBenefitForm, setShowBenefitForm] = useState(false)
  const [editingBenefit, setEditingBenefit] = useState<BenefitItem | null>(null)
  const [benefitForm, setBenefitForm] = useState({
    name: '',
    logoUrl: '',
    headline: '',
    description: '',
    redemptionType: 'StaticCode',
    voucherCode: '',
    flyerUrl: '',
    websiteUrl: '',
    howToSteps: '',
    attachmentsJson: '',
    barcodeUrl: '',
    isActive: true,
    sortOrder: 0,
  })
  const [savingBenefit, setSavingBenefit] = useState(false)
  const benefitFormRef = useRef<HTMLDivElement>(null)
  const [pendingEditBenefit, setPendingEditBenefit] = useState<BenefitItem | null>(null)
  const [uploadingFlyer, setUploadingFlyer] = useState(false)
  const [uploadingBarcode, setUploadingBarcode] = useState(false)
  const [uploadingAttachment, setUploadingAttachment] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const flyerInputRef = useRef<HTMLInputElement>(null)
  const barcodeInputRef = useRef<HTMLInputElement>(null)
  const attachmentInputRef = useRef<HTMLInputElement>(null)
  const logoInputRef = useRef<HTMLInputElement>(null)

  // Banners
  const [banners, setBanners] = useState<BannerItem[]>([])
  const [bannersLoading, setBannersLoading] = useState(false)
  const [bannerSaving, setBannerSaving] = useState(false)
  const [bannerDialogScreen, setBannerDialogScreen] = useState<string | null>(null)
  const [bannerDraft, setBannerDraft] = useState<BannerItem | null>(null)

  const uploadFile = async (
    file: File,
    type: 'pdf' | 'barcode' | 'logo',
  ): Promise<string | null> => {
    const form = new FormData()
    form.append('email', userEmail)
    form.append('type', type)
    form.append('file', file)
    const r = await fetch('/api/memberbenefits/upload', { method: 'POST', body: form })
    if (!r.ok) {
      toast({ title: 'Upload failed', variant: 'destructive' })
      return null
    }
    const d = await r.json()
    return d.path as string
  }

  const handleFlyerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingFlyer(true)
    const path = await uploadFile(file, 'pdf')
    if (path) setBenefitForm((p) => ({ ...p, flyerUrl: path }))
    setUploadingFlyer(false)
    e.target.value = ''
  }

  const handleBarcodeUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingBarcode(true)
    const path = await uploadFile(file, 'barcode')
    if (path) setBenefitForm((p) => ({ ...p, barcodeUrl: path }))
    setUploadingBarcode(false)
    e.target.value = ''
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingLogo(true)
    const path = await uploadFile(file, 'logo')
    if (path) setBenefitForm((p) => ({ ...p, logoUrl: path }))
    setUploadingLogo(false)
    e.target.value = ''
  }

  const handleAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingAttachment(true)
    const path = await uploadFile(file, 'pdf')
    if (path) {
      const label = file.name.replace(/\.[^.]+$/, '').replace(/_/g, ' ')
      const existing: { label: string; url: string }[] = (() => {
        try {
          return JSON.parse(benefitForm.attachmentsJson || '[]')
        } catch {
          return []
        }
      })()
      existing.push({ label, url: path })
      setBenefitForm((p) => ({ ...p, attachmentsJson: JSON.stringify(existing) }))
    }
    setUploadingAttachment(false)
    e.target.value = ''
  }

  const removeAttachment = (idx: number) => {
    const existing: { label: string; url: string }[] = (() => {
      try {
        return JSON.parse(benefitForm.attachmentsJson || '[]')
      } catch {
        return []
      }
    })()
    existing.splice(idx, 1)
    setBenefitForm((p) => ({
      ...p,
      attachmentsJson: existing.length ? JSON.stringify(existing) : '',
    }))
  }

  const parsedAttachments = (() => {
    try {
      return JSON.parse(benefitForm.attachmentsJson || '[]') as { label: string; url: string }[]
    } catch {
      return []
    }
  })()

  const fetchBenefits = async () => {
    setBenefitsLoading(true)
    try {
      const r = await fetch(`/api/memberbenefits/all?email=${encodeURIComponent(userEmail)}`)
      const d = await r.json()
      setBenefits(d)
    } catch {
      toast({ title: 'Failed to load benefits', variant: 'destructive' })
    } finally {
      setBenefitsLoading(false)
    }
  }

  const scrollToForm = () => {
    setTimeout(() => {
      benefitFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)
  }

  const openNewBenefitForm = () => {
    setEditingBenefit(null)
    setBenefitForm({
      name: '',
      logoUrl: '',
      headline: '',
      description: '',
      redemptionType: 'StaticCode',
      voucherCode: '',
      flyerUrl: '',
      websiteUrl: '',
      howToSteps: '',
      attachmentsJson: '',
      barcodeUrl: '',
      isActive: true,
      sortOrder: benefits.length > 0 ? Math.max(...benefits.map((b) => b.sortOrder)) + 1 : 1,
    })
    setShowBenefitForm(true)
    scrollToForm()
  }

  const applyEditBenefitForm = (b: BenefitItem) => {
    setEditingBenefit(b)
    setBenefitForm({
      name: b.name,
      logoUrl: b.logoUrl ?? '',
      headline: b.headline,
      description: b.description ?? '',
      redemptionType: b.redemptionType,
      voucherCode: b.voucherCode ?? '',
      flyerUrl: b.flyerUrl ?? '',
      websiteUrl: b.websiteUrl ?? '',
      howToSteps: b.howToSteps ?? '',
      attachmentsJson: b.attachmentsJson ?? '',
      barcodeUrl: b.barcodeUrl ?? '',
      isActive: b.isActive,
      sortOrder: b.sortOrder,
    })
    setShowBenefitForm(true)
    scrollToForm()
  }

  const openEditBenefitForm = (b: BenefitItem) => {
    // If a different benefit is already open in the form, ask before discarding
    if (showBenefitForm && editingBenefit?.id !== b.id) {
      setPendingEditBenefit(b)
      scrollToForm()
      return
    }
    applyEditBenefitForm(b)
  }

  const handleSaveBenefit = async () => {
    if (!benefitForm.name.trim())
      return toast({ title: 'Name is required', variant: 'destructive' })
    if (!benefitForm.headline.trim())
      return toast({ title: 'Headline is required', variant: 'destructive' })
    setSavingBenefit(true)
    try {
      const r = await fetch('/api/memberbenefits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          callerEmail: userEmail,
          id: editingBenefit?.id ?? null,
          name: benefitForm.name.trim(),
          logoUrl: benefitForm.logoUrl.trim() || null,
          headline: benefitForm.headline.trim(),
          description: benefitForm.description.trim() || null,
          redemptionType: benefitForm.redemptionType,
          voucherCode: benefitForm.voucherCode.trim() || null,
          flyerUrl: benefitForm.flyerUrl.trim() || null,
          websiteUrl: benefitForm.websiteUrl.trim() || null,
          howToSteps: benefitForm.howToSteps.trim() || null,
          attachmentsJson: benefitForm.attachmentsJson.trim() || null,
          barcodeUrl: benefitForm.barcodeUrl.trim() || null,
          isActive: benefitForm.isActive,
          sortOrder: Number(benefitForm.sortOrder),
        }),
      })
      if (!r.ok) throw new Error()
      toast({ title: editingBenefit ? 'Benefit updated!' : 'Benefit created!' })
      setShowBenefitForm(false)
      fetchBenefits()
    } catch {
      toast({ title: 'Failed to save benefit', variant: 'destructive' })
    } finally {
      setSavingBenefit(false)
    }
  }

  const handleToggleBenefit = async (id: number, current: boolean) => {
    try {
      await fetch(`/api/memberbenefits/${id}/toggle`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ callerEmail: userEmail, isActive: !current }),
      })
      setBenefits((prev) => prev.map((b) => (b.id === id ? { ...b, isActive: !current } : b)))
    } catch {
      toast({ title: 'Failed to update benefit', variant: 'destructive' })
    }
  }

  useEffect(() => {
    fetchStats()
  }, [])

  const fetchStats = async () => {
    setStatsLoading(true)
    try {
      const r = await fetch(`/api/admin/stats?email=${encodeURIComponent(userEmail)}`)
      const d = await r.json()
      setStats(d)
    } catch {
      // silent
    } finally {
      setStatsLoading(false)
    }
  }

  const fetchQuestions = async () => {
    setQuestionsLoading(true)
    try {
      const r = await fetch(`/api/admin/questions?email=${encodeURIComponent(userEmail)}`)
      const d = await r.json()
      setQuestions(d)
    } catch {
      toast({ title: 'Failed to load questions', variant: 'destructive' })
    } finally {
      setQuestionsLoading(false)
    }
  }

  const fetchTeam = async () => {
    setTeamLoading(true)
    try {
      const r = await fetch(`/api/admin/team?email=${encodeURIComponent(userEmail)}`)
      const d = await r.json()
      setAdmins(d)
    } catch {
      toast({ title: 'Failed to load team', variant: 'destructive' })
    } finally {
      setTeamLoading(false)
    }
  }

  const handleToggleQuestion = async (id: number, current: boolean) => {
    try {
      await fetch(`/api/admin/questions/${id}/toggle`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ callerEmail: userEmail, isActive: !current }),
      })
      setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, isActive: !current } : q)))
    } catch {
      toast({ title: 'Failed to update question', variant: 'destructive' })
    }
  }

  const handleCreateQuestion = async () => {
    if (!newQuestion.trim())
      return toast({ title: 'Question text is required', variant: 'destructive' })
    const validOptions = newOptions.filter((o) => o.value.trim() && o.label.trim())
    if (validOptions.length < 2)
      return toast({ title: 'At least 2 options required', variant: 'destructive' })

    setSaving(true)
    try {
      const r = await fetch('/api/admin/questions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          callerEmail: userEmail,
          question: newQuestion.trim(),
          activeDate: newActiveDate || null,
          options: validOptions.map((o, i) => ({
            optionValue: o.value.trim(),
            optionLabel: o.label.trim(),
            displayOrder: i + 1,
          })),
        }),
      })
      if (!r.ok) throw new Error()
      toast({ title: 'Question created!' })
      setShowAddQuestion(false)
      setNewQuestion('')
      setNewActiveDate('')
      setNewOptions([
        { value: '', label: '' },
        { value: '', label: '' },
      ])
      fetchQuestions()
      fetchStats()
    } catch {
      toast({ title: 'Failed to create question', variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  const handleAddAdmin = async () => {
    if (!newAdminEmail.trim() || !newAdminName.trim())
      return toast({ title: 'Email and name are required', variant: 'destructive' })
    setAddingAdmin(true)
    try {
      const r = await fetch('/api/admin/team', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          callerEmail: userEmail,
          email: newAdminEmail.trim(),
          name: newAdminName.trim(),
        }),
      })
      if (!r.ok) throw new Error()
      toast({ title: 'Admin added!' })
      setNewAdminEmail('')
      setNewAdminName('')
      fetchTeam()
      fetchStats()
    } catch {
      toast({ title: 'Failed to add admin', variant: 'destructive' })
    } finally {
      setAddingAdmin(false)
    }
  }

  const handleToggleAdmin = async (id: number, current: boolean) => {
    try {
      await fetch(`/api/admin/team/${id}/toggle`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ callerEmail: userEmail, isActive: !current }),
      })
      setAdmins((prev) => prev.map((a) => (a.id === id ? { ...a, isActive: !current } : a)))
    } catch {
      toast({ title: 'Failed to update admin', variant: 'destructive' })
    }
  }

  const fetchRecentNotifs = async () => {
    setRecentNotifsLoading(true)
    try {
      const [notifsRes, regionsRes] = await Promise.all([
        fetch(`/api/notifications/recent?email=${encodeURIComponent(userEmail)}`),
        fetch('api/report/region?region=all'),
      ])
      if (notifsRes.ok) setRecentNotifs(await notifsRes.json())
      if (regionsRes.ok) {
        const all: { regionId: number; region: string }[] = await regionsRes.json()
        setNotifRegions(
          all
            .filter((r) => r.regionId < 50)
            .map((r) => r.region.replace(/ Region$/i, '').trim())
            .filter((v, i, a) => a.indexOf(v) === i)
            .sort(),
        )
      }
      // Load distinct organisations from user preferences
      const orgsRes = await fetch(
        `/api/notifications/organisations?email=${encodeURIComponent(userEmail)}`,
      )
      if (orgsRes.ok) setNotifOrgs(await orgsRes.json())
    } catch {
      /* silent */
    } finally {
      setRecentNotifsLoading(false)
    }
  }

  const handlePurgeCache = async () => {
    if (
      !window.confirm(
        'This will clear all market insights, region, suburb and suburb-sales cache. Members will see fresh data on next load. Continue?',
      )
    )
      return
    setCachePurging(true)
    try {
      const r = await fetch(`/api/admin/cache/purge?email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error)
      toast({
        title: 'Cache cleared',
        description: `${d.cleared} key${d.cleared !== 1 ? 's' : ''} removed. Members will fetch fresh data.`,
      })
    } catch (e: any) {
      toast({ title: 'Purge failed', description: e.message, variant: 'destructive' })
    } finally {
      setCachePurging(false)
    }
  }

  const handleBroadcast = async () => {
    if (!notifTitle.trim()) return toast({ title: 'Title is required', variant: 'destructive' })
    if (notifTarget !== 'all' && !notifTargetValue)
      return toast({ title: 'Please select a target value', variant: 'destructive' })
    setNotifSending(true)
    try {
      const r = await fetch('/api/notifications/broadcast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          callerEmail: userEmail,
          title: notifTitle.trim(),
          body: notifBody.trim() || null,
          url: notifUrl.trim() || null,
          target: notifTarget,
          targetValue: notifTarget !== 'all' ? notifTargetValue : null,
        }),
      })
      if (!r.ok) throw new Error()
      const targetLabel =
        notifTarget === 'all' ? 'all subscribers' : `${notifTarget}: ${notifTargetValue}`
      toast({ title: 'Notification sent!', description: `Sent to ${targetLabel}.` })
      setNotifTitle('')
      setNotifBody('')
      setNotifUrl('')
      setNotifTarget('all')
      setNotifTargetValue('')
      fetchRecentNotifs()
    } catch {
      toast({ title: 'Failed to send notification', variant: 'destructive' })
    } finally {
      setNotifSending(false)
    }
  }

  return (
    <Tabs
      defaultValue="overview"
      onValueChange={(v) => {
        if (v === 'survey') fetchQuestions()
        if (v === 'team') fetchTeam()
        if (v === 'benefits') fetchBenefits()
        if (v === 'notifications') fetchRecentNotifs()
        if (v === 'banner') {
          setBannersLoading(true)
          fetch(`/api/admin/banners?email=${encodeURIComponent(userEmail)}`)
            .then((r) => (r.ok ? r.json() : []))
            .then((d) => setBanners(Array.isArray(d) ? d : []))
            .catch(() => {})
            .finally(() => setBannersLoading(false))
        }
      }}
    >
      {/* Sticky header */}
      <div className="sticky top-0 z-30 bg-background border-b">
        <div className="flex items-center gap-3 px-4 pt-4 pb-2">
          <button onClick={onBack} className="p-1 rounded-full hover:bg-muted">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            <span className="font-semibold text-foreground">Admin Panel</span>
          </div>
          <Badge variant="secondary" className="ml-auto text-xs">
            Internal
          </Badge>
        </div>
        <TabsList className="w-full rounded-none border-0 bg-transparent h-auto px-4 pb-0 gap-0 justify-start">
          <TabsTrigger
            value="overview"
            className="text-xs px-3 py-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="survey"
            className="text-xs px-3 py-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
          >
            Survey
          </TabsTrigger>
          <TabsTrigger
            value="notifications"
            className="text-xs px-3 py-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
          >
            Notifications
          </TabsTrigger>
          <TabsTrigger
            value="team"
            className="text-xs px-3 py-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
          >
            Team
          </TabsTrigger>
          <TabsTrigger
            value="benefits"
            className="text-xs px-3 py-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
          >
            Member Perks
          </TabsTrigger>
          <TabsTrigger
            value="banner"
            className="text-xs px-3 py-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
          >
            Banners
          </TabsTrigger>
        </TabsList>
      </div>

      {/* Overview */}
      <TabsContent value="overview" className="px-4 py-4 pb-20 space-y-4 mt-0">
        {statsLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : stats ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Card className="p-4 space-y-1">
                <p className="text-xs text-muted-foreground">Total Responses</p>
                <p className="text-2xl font-bold text-foreground">{stats.responsesTotal}</p>
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <TrendingUp className="w-3 h-3" />
                  All time
                </div>
              </Card>
              <Card className="p-4 space-y-1">
                <p className="text-xs text-muted-foreground">Today</p>
                <p className="text-2xl font-bold text-foreground">{stats.responsesToday}</p>
                <div className="flex items-center gap-1 text-xs text-blue-600">
                  <Activity className="w-3 h-3" />
                  Responses today
                </div>
              </Card>
              <Card className="p-4 space-y-1">
                <p className="text-xs text-muted-foreground">Active Questions</p>
                <p className="text-2xl font-bold text-foreground">{stats.activeQuestions}</p>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <BookOpen className="w-3 h-3" />
                  In rotation
                </div>
              </Card>
              <Card className="p-4 space-y-1">
                <p className="text-xs text-muted-foreground">Unique Members</p>
                <p className="text-2xl font-bold text-foreground">{stats.uniqueRespondents}</p>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Users className="w-3 h-3" />
                  Ever responded
                </div>
              </Card>
            </div>

            <Card className="p-4 space-y-3">
              <p className="text-sm font-medium text-foreground">Quick Actions</p>
              <div className="space-y-2">
                <button
                  className="w-full flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted text-left transition-colors"
                  onClick={() => {
                    document
                      .querySelector('[data-value="survey"]')
                      ?.dispatchEvent(new MouseEvent('click', { bubbles: true }))
                  }}
                >
                  <BarChart3 className="w-4 h-4 text-primary shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-foreground">Manage Survey Questions</p>
                    <p className="text-xs text-muted-foreground">
                      Add, schedule or deactivate questions
                    </p>
                  </div>
                </button>
                <button
                  className="w-full flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted text-left transition-colors"
                  onClick={() => {
                    document
                      .querySelector('[data-value="notifications"]')
                      ?.dispatchEvent(new MouseEvent('click', { bubbles: true }))
                  }}
                >
                  <Bell className="w-4 h-4 text-primary shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-foreground">Send Push Notification</p>
                    <p className="text-xs text-muted-foreground">
                      Broadcast to all members or a region
                    </p>
                  </div>
                </button>
                <button
                  className="w-full flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted text-left transition-colors"
                  onClick={() => {
                    document
                      .querySelector('[data-value="team"]')
                      ?.dispatchEvent(new MouseEvent('click', { bubbles: true }))
                  }}
                >
                  <Users className="w-4 h-4 text-primary shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-foreground">Manage Admin Team</p>
                    <p className="text-xs text-muted-foreground">
                      {stats.totalAdmins} active admin{stats.totalAdmins !== 1 ? 's' : ''}
                    </p>
                  </div>
                </button>
                <button
                  className="w-full flex items-center gap-3 p-3 rounded-lg bg-orange-50 hover:bg-orange-100 text-left transition-colors disabled:opacity-50"
                  onClick={handlePurgeCache}
                  disabled={cachePurging}
                >
                  {cachePurging ? (
                    <Loader2 className="w-4 h-4 text-orange-600 shrink-0 animate-spin" />
                  ) : (
                    <DatabaseZap className="w-4 h-4 text-orange-600 shrink-0" />
                  )}
                  <div>
                    <p className="text-xs font-medium text-foreground">Purge Market Data Cache</p>
                    <p className="text-xs text-muted-foreground">
                      Use after monthly data release — forces fresh fetch
                    </p>
                  </div>
                </button>
              </div>
            </Card>
          </>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-10">Failed to load stats.</p>
        )}
      </TabsContent>

      {/* Survey */}
      <TabsContent value="survey" className="px-4 py-4 pb-20 space-y-4 mt-0">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-foreground">Survey Questions</p>
          <Button
            size="sm"
            onClick={() => setShowAddQuestion((v) => !v)}
            className="h-8 text-xs gap-1"
          >
            <Plus className="w-3 h-3" />
            Add Question
          </Button>
        </div>

        {/* Add Question Form */}
        {showAddQuestion && (
          <Card className="p-4 space-y-3 border-primary/30 bg-primary/5">
            <p className="text-xs font-semibold text-foreground">New Question</p>
            <Textarea
              placeholder="Enter your question..."
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              className="text-sm resize-none"
              rows={2}
            />
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Schedule date (optional)
              </label>
              <Input
                type="date"
                value={newActiveDate}
                onChange={(e) => setNewActiveDate(e.target.value)}
                className="text-sm h-8"
              />
            </div>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Options</p>
              {newOptions.map((opt, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    placeholder={`Value (e.g. option_${i + 1})`}
                    value={opt.value}
                    onChange={(e) =>
                      setNewOptions((prev) =>
                        prev.map((o, j) => (j === i ? { ...o, value: e.target.value } : o)),
                      )
                    }
                    className="text-xs h-8 flex-1"
                  />
                  <Input
                    placeholder="Label"
                    value={opt.label}
                    onChange={(e) =>
                      setNewOptions((prev) =>
                        prev.map((o, j) => (j === i ? { ...o, label: e.target.value } : o)),
                      )
                    }
                    className="text-xs h-8 flex-1"
                  />
                  {newOptions.length > 2 && (
                    <button
                      onClick={() => setNewOptions((prev) => prev.filter((_, j) => j !== i))}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {newOptions.length < 6 && (
                <button
                  className="text-xs text-primary flex items-center gap-1"
                  onClick={() => setNewOptions((prev) => [...prev, { value: '', label: '' }])}
                >
                  <Plus className="w-3 h-3" /> Add option
                </button>
              )}
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                onClick={handleCreateQuestion}
                disabled={saving}
                className="flex-1 text-xs h-8"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save Question'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowAddQuestion(false)}
                className="text-xs h-8"
              >
                Cancel
              </Button>
            </div>
          </Card>
        )}

        {questionsLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : questions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">No questions yet.</p>
        ) : (
          <div className="space-y-3">
            {questions.map((q) => (
              <Card key={q.id} className={`overflow-hidden ${!q.isActive ? 'opacity-60' : ''}`}>
                <div
                  className="flex items-start gap-3 p-3 cursor-pointer"
                  onClick={() => setExpandedId(expandedId === q.id ? null : q.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant={q.isActive ? 'default' : 'secondary'} className="text-3xs">
                        {q.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      {q.activeDate && (
                        <Badge variant="outline" className="text-3xs gap-1">
                          <Calendar className="w-2.5 h-2.5" />
                          {new Date(q.activeDate).toLocaleDateString()}
                        </Badge>
                      )}
                      <span className="text-3xs text-muted-foreground ml-auto">
                        {q.responseCount} responses
                      </span>
                    </div>
                    <p className="text-xs font-medium text-foreground leading-snug line-clamp-2">
                      {q.question}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Switch
                      checked={q.isActive}
                      onCheckedChange={() => handleToggleQuestion(q.id, q.isActive)}
                      onClick={(e) => e.stopPropagation()}
                      className="scale-75"
                    />
                    {expandedId === q.id ? (
                      <ChevronUp className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                </div>
                {expandedId === q.id && (
                  <div className="border-t px-3 py-3 bg-muted/30 space-y-1.5">
                    {q.options.map((o) => (
                      <div key={o.id} className="flex justify-between text-xs">
                        <span className="text-muted-foreground">{o.optionLabel}</span>
                        <span className="font-mono text-muted-foreground">{o.optionValue}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </TabsContent>

      {/* Notifications */}
      <TabsContent value="notifications" className="px-4 py-4 pb-20 space-y-4 mt-0">
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-primary" />
            <p className="text-sm font-medium text-foreground">Compose Notification</p>
          </div>
          <Input
            placeholder="Title (e.g. Survey closing in 5 days)"
            value={notifTitle}
            onChange={(e) => setNotifTitle(e.target.value)}
            className="text-sm"
          />
          <Textarea
            placeholder="Message body..."
            value={notifBody}
            onChange={(e) => setNotifBody(e.target.value)}
            rows={3}
            className="text-sm resize-none"
          />
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              Link URL <span className="text-muted-foreground/60">(optional)</span>
            </label>
            <Input
              placeholder="https://reinz.co.nz/... or leave blank"
              value={notifUrl}
              onChange={(e) => setNotifUrl(e.target.value)}
              className="text-sm"
              type="url"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Target Audience</label>
            <select
              value={notifTarget}
              onChange={(e) => {
                setNotifTarget(e.target.value as 'all' | 'region' | 'organisation')
                setNotifTargetValue('')
              }}
              className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background"
            >
              <option value="all">All Members</option>
              <option value="region">By Region</option>
              <option value="organisation">By Organisation</option>
            </select>
          </div>
          {notifTarget === 'region' && (
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Select Region</label>
              <select
                value={notifTargetValue}
                onChange={(e) => setNotifTargetValue(e.target.value)}
                className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background"
              >
                <option value="">— choose a region —</option>
                {notifRegions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          )}
          {notifTarget === 'organisation' && (
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Select Organisation</label>
              <select
                value={notifTargetValue}
                onChange={(e) => setNotifTargetValue(e.target.value)}
                className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background"
              >
                <option value="">— choose an organisation —</option>
                {notifOrgs.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Button className="w-full gap-2" onClick={handleBroadcast} disabled={notifSending}>
            {notifSending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {notifSending
              ? 'Sending…'
              : notifTarget === 'all'
                ? 'Send to All Members'
                : `Send to ${notifTarget === 'region' ? 'Region' : 'Organisation'}`}
          </Button>
        </Card>

        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Recent Notifications
          </p>
          {recentNotifsLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : recentNotifs.length === 0 ? (
            <Card className="p-6 flex flex-col items-center justify-center text-center space-y-2">
              <Bell className="w-8 h-8 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No notifications yet</p>
              <p className="text-xs text-muted-foreground">
                Sent notifications and auto-synced items will appear here
              </p>
            </Card>
          ) : (
            <div className="space-y-2">
              {recentNotifs.map((n) => (
                <Card key={n.id} className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span
                          className={`text-3xs font-semibold px-1.5 py-0.5 rounded-full ${n.source === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}
                        >
                          {n.source === 'admin'
                            ? 'Manual'
                            : n.type.charAt(0).toUpperCase() + n.type.slice(1)}
                        </span>
                        <span className="text-3xs text-muted-foreground">
                          {new Date(n.publishedAt).toLocaleDateString('en-NZ', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                      <p className="text-xs font-medium text-foreground truncate">{n.title}</p>
                      {n.body && (
                        <p className="text-3xs text-muted-foreground mt-0.5 line-clamp-1">
                          {n.body}
                        </p>
                      )}
                      {n.url && <p className="text-3xs text-primary mt-0.5 truncate">{n.url}</p>}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </TabsContent>

      {/* Team */}
      <TabsContent value="team" className="px-4 py-4 pb-20 space-y-4 mt-0">
        <Card className="p-4 space-y-3 border-primary/30 bg-primary/5">
          <p className="text-xs font-semibold text-foreground">Add Admin</p>
          <Input
            placeholder="Email address"
            type="email"
            value={newAdminEmail}
            onChange={(e) => setNewAdminEmail(e.target.value)}
            className="text-sm"
          />
          <Input
            placeholder="Full name"
            value={newAdminName}
            onChange={(e) => setNewAdminName(e.target.value)}
            className="text-sm"
          />
          <Button
            size="sm"
            onClick={handleAddAdmin}
            disabled={addingAdmin}
            className="w-full text-xs h-8 gap-1"
          >
            {addingAdmin ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <>
                <Plus className="w-3 h-3" /> Add Admin
              </>
            )}
          </Button>
        </Card>

        {teamLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Current Admins
            </p>
            {admins.map((a) => (
              <Card
                key={a.id}
                className={`p-3 flex items-center gap-3 ${!a.isActive ? 'opacity-60' : ''}`}
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm shrink-0">
                  {a.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{a.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{a.email}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant={a.isActive ? 'default' : 'secondary'} className="text-3xs">
                    {a.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                  <Switch
                    checked={a.isActive}
                    onCheckedChange={() => handleToggleAdmin(a.id, a.isActive)}
                    className="scale-75"
                  />
                </div>
              </Card>
            ))}
          </div>
        )}
      </TabsContent>

      {/* Benefits */}
      <TabsContent value="benefits" className="px-4 py-4 pb-20 space-y-4 mt-0">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-foreground">Member Perks</p>
          <Button size="sm" onClick={openNewBenefitForm} className="h-8 text-xs gap-1">
            <Plus className="w-3 h-3" />
            Add Benefit
          </Button>
        </div>

        {/* Add / Edit form */}
        {showBenefitForm && (
          <Card ref={benefitFormRef} className="p-4 space-y-3 border-primary/30 bg-primary/5">
            <p className="text-xs font-semibold text-foreground">
              {editingBenefit ? `Edit: ${editingBenefit.name}` : 'New Benefit'}
            </p>

            {/* Keep / Discard prompt when another pencil was clicked */}
            {pendingEditBenefit && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 space-y-2">
                <p className="text-xs font-medium text-amber-800">
                  Switch to editing <span className="font-bold">{pendingEditBenefit.name}</span>?
                </p>
                <p className="text-3xs text-amber-700">
                  You have unsaved changes. Discard them and open the new benefit, or keep editing
                  the current one.
                </p>
                <div className="flex gap-2 pt-0.5">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-xs h-7 border-amber-400 text-amber-800 hover:bg-amber-100"
                    onClick={() => setPendingEditBenefit(null)}
                  >
                    Keep editing
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 text-xs h-7 bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => {
                      const b = pendingEditBenefit
                      setPendingEditBenefit(null)
                      applyEditBenefitForm(b)
                    }}
                  >
                    Discard &amp; switch
                  </Button>
                </div>
              </div>
            )}

            <Input
              placeholder="Name (e.g. Advantage Tyre)"
              value={benefitForm.name}
              onChange={(e) => setBenefitForm((p) => ({ ...p, name: e.target.value }))}
              className="text-sm"
            />

            {/* Logo upload */}
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Logo</label>
              <input
                ref={logoInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp,.svg"
                className="hidden"
                onChange={handleLogoUpload}
              />
              <div className="flex items-center gap-2">
                {benefitForm.logoUrl && (
                  <img
                    src={benefitForm.logoUrl}
                    alt="logo preview"
                    className="w-10 h-10 rounded object-contain border border-border bg-white p-0.5 shrink-0"
                  />
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-xs h-8 gap-1.5 shrink-0"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Image className="w-3 h-3" />
                  )}
                  {uploadingLogo
                    ? 'Uploading…'
                    : benefitForm.logoUrl
                      ? 'Replace logo'
                      : 'Upload logo'}
                </Button>
                {benefitForm.logoUrl && (
                  <button
                    onClick={() => setBenefitForm((p) => ({ ...p, logoUrl: '' }))}
                    className="text-muted-foreground hover:text-destructive shrink-0"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            <Input
              placeholder="Headline (e.g. 15% off tyres)"
              value={benefitForm.headline}
              onChange={(e) => setBenefitForm((p) => ({ ...p, headline: e.target.value }))}
              className="text-sm"
            />
            <Textarea
              placeholder="Description (optional)"
              value={benefitForm.description}
              onChange={(e) => setBenefitForm((p) => ({ ...p, description: e.target.value }))}
              className="text-sm resize-none"
              rows={2}
            />

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Redemption Type</label>
              <div className="grid grid-cols-2 gap-2">
                {REDEMPTION_TYPES.map((t) => (
                  <button
                    key={t}
                    onClick={() => setBenefitForm((p) => ({ ...p, redemptionType: t }))}
                    className={`text-xs py-1.5 px-2 rounded-lg border transition-colors ${
                      benefitForm.redemptionType === t
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {benefitForm.redemptionType === 'StaticCode' && (
              <Input
                placeholder="Voucher Code (e.g. REINZ)"
                value={benefitForm.voucherCode}
                onChange={(e) => setBenefitForm((p) => ({ ...p, voucherCode: e.target.value }))}
                className="text-sm font-mono"
              />
            )}
            {benefitForm.redemptionType === 'Flyer' && (
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">Flyer PDF</label>
                <input
                  ref={flyerInputRef}
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={handleFlyerUpload}
                />
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="text-xs h-8 gap-1.5 shrink-0"
                    onClick={() => flyerInputRef.current?.click()}
                    disabled={uploadingFlyer}
                  >
                    {uploadingFlyer ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <FileText className="w-3 h-3" />
                    )}
                    {uploadingFlyer ? 'Uploading…' : 'Upload PDF'}
                  </Button>
                  {benefitForm.flyerUrl && (
                    <>
                      <span className="text-xs text-muted-foreground truncate flex-1">
                        {benefitForm.flyerUrl}
                      </span>
                      <button
                        onClick={() => setBenefitForm((p) => ({ ...p, flyerUrl: '' }))}
                        className="text-muted-foreground hover:text-destructive shrink-0"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
            <Input
              placeholder="Website URL (optional)"
              value={benefitForm.websiteUrl}
              onChange={(e) => setBenefitForm((p) => ({ ...p, websiteUrl: e.target.value }))}
              className="text-sm"
            />
            <Textarea
              placeholder={`How-to steps as JSON array\ne.g. ["Step 1","Step 2","Step 3"]`}
              value={benefitForm.howToSteps}
              onChange={(e) => setBenefitForm((p) => ({ ...p, howToSteps: e.target.value }))}
              className="text-xs font-mono resize-none"
              rows={3}
            />

            {/* Attachments — upload PDFs and see them as a list */}
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Attachments (PDF buttons)</label>
              <input
                ref={attachmentInputRef}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={handleAttachmentUpload}
              />
              {parsedAttachments.length > 0 && (
                <div className="space-y-1">
                  {parsedAttachments.map((a, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 bg-muted/50 rounded px-2 py-1.5"
                    >
                      <FileText className="w-3 h-3 text-muted-foreground shrink-0" />
                      <Input
                        value={a.label}
                        onChange={(e) => {
                          const updated = [...parsedAttachments]
                          updated[idx] = { ...updated[idx], label: e.target.value }
                          setBenefitForm((p) => ({
                            ...p,
                            attachmentsJson: JSON.stringify(updated),
                          }))
                        }}
                        className="text-xs h-6 flex-1 bg-transparent border-none p-0 focus-visible:ring-0"
                        placeholder="Button label"
                      />
                      <span className="text-3xs text-muted-foreground truncate max-w-[100px]">
                        {a.url}
                      </span>
                      <button
                        onClick={() => removeAttachment(idx)}
                        className="text-muted-foreground hover:text-destructive shrink-0"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="text-xs h-8 gap-1.5"
                onClick={() => attachmentInputRef.current?.click()}
                disabled={uploadingAttachment}
              >
                {uploadingAttachment ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Upload className="w-3 h-3" />
                )}
                {uploadingAttachment ? 'Uploading…' : 'Upload PDF attachment'}
              </Button>
            </div>

            {/* Barcode image upload */}
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Barcode image (optional)</label>
              <input
                ref={barcodeInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp"
                className="hidden"
                onChange={handleBarcodeUpload}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-xs h-8 gap-1.5 shrink-0"
                  onClick={() => barcodeInputRef.current?.click()}
                  disabled={uploadingBarcode}
                >
                  {uploadingBarcode ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Image className="w-3 h-3" />
                  )}
                  {uploadingBarcode ? 'Uploading…' : 'Upload barcode'}
                </Button>
                {benefitForm.barcodeUrl && (
                  <>
                    <span className="text-xs text-muted-foreground truncate flex-1">
                      {benefitForm.barcodeUrl}
                    </span>
                    <button
                      onClick={() => setBenefitForm((p) => ({ ...p, barcodeUrl: '' }))}
                      className="text-muted-foreground hover:text-destructive shrink-0"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min={1}
                placeholder="Sort order"
                value={benefitForm.sortOrder}
                onChange={(e) =>
                  setBenefitForm((p) => ({ ...p, sortOrder: Math.max(1, Number(e.target.value)) }))
                }
                className="text-sm w-24"
              />
              <label className="flex items-center gap-2 text-xs text-foreground">
                <Switch
                  checked={benefitForm.isActive}
                  onCheckedChange={(v) => setBenefitForm((p) => ({ ...p, isActive: v }))}
                  className="scale-75"
                />
                Active
              </label>
            </div>

            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                onClick={handleSaveBenefit}
                disabled={savingBenefit}
                className="flex-1 text-xs h-8"
              >
                {savingBenefit ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : editingBenefit ? (
                  'Update'
                ) : (
                  'Create'
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowBenefitForm(false)}
                className="text-xs h-8"
              >
                Cancel
              </Button>
            </div>
          </Card>
        )}

        {benefitsLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : benefits.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">No benefits yet.</p>
        ) : (
          <div className="space-y-3">
            {benefits.map((b) => (
              <Card key={b.id} className={`p-3 ${!b.isActive ? 'opacity-60' : ''}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <p className="text-sm font-medium text-foreground">{b.name}</p>
                      <span className="text-3xs px-1.5 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                        {b.redemptionType}
                      </span>
                      {b.voucherCode && (
                        <span className="text-3xs font-mono text-muted-foreground">
                          "{b.voucherCode}"
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-1">{b.headline}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => openEditBenefitForm(b)}
                      className="p-1 rounded hover:bg-muted text-muted-foreground"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <Switch
                      checked={b.isActive}
                      onCheckedChange={() => handleToggleBenefit(b.id, b.isActive)}
                      className="scale-75"
                    />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </TabsContent>

      {/* Banners */}
      <TabsContent value="banner" className="px-4 py-4 pb-20 space-y-4 mt-0">
        <h2 className="text-sm font-semibold text-foreground">Service Banners</h2>
        <p className="text-xs text-muted-foreground -mt-2">
          Show a banner on any screen when a service is disrupted. Global affects all screens.
        </p>
        {bannersLoading ? (
          <div className="space-y-2 animate-pulse">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-14 bg-muted rounded-xl" />
            ))}
          </div>
        ) : (
          <Card className="divide-y divide-border">
            {BANNER_SCREENS.map(({ key, label }) => {
              const item = banners.find((b) => b.screen === key) ?? {
                screen: key,
                isActive: false,
                message: '',
                bannerType: 'warning' as const,
              }
              return (
                <div key={key} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-2 h-2 rounded-full shrink-0 ${item.isActive ? 'bg-green-500' : 'bg-muted-foreground/30'}`}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {item.isActive ? item.message || 'Active — no message set' : 'Off'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setBannerDialogScreen(key)
                      setBannerDraft({ ...item })
                    }}
                    className="ml-4 p-1.5 rounded hover:bg-muted text-muted-foreground shrink-0"
                    title={`Edit ${label} banner`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
              )
            })}
          </Card>
        )}

        {/* Banner edit dialog */}
        <Dialog
          open={!!bannerDialogScreen}
          onOpenChange={(open) => {
            if (!open) {
              setBannerDialogScreen(null)
              setBannerDraft(null)
            }
          }}
        >
          <DialogContent
            onInteractOutside={(e) => {
              if (bannerSaving) e.preventDefault()
            }}
            onEscapeKeyDown={(e) => {
              if (bannerSaving) e.preventDefault()
            }}
          >
            <DialogHeader>
              <DialogTitle>
                {BANNER_SCREENS.find((s) => s.key === bannerDialogScreen)?.label ?? ''} Banner
              </DialogTitle>
            </DialogHeader>

            {bannerDraft && (
              <div className="space-y-4 py-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Show banner</p>
                    <p className="text-xs text-muted-foreground">
                      Toggle on to display this banner to users
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {bannerDraft.isActive ? 'Active' : 'Off'}
                    </span>
                    <Switch
                      checked={bannerDraft.isActive}
                      onCheckedChange={(val) =>
                        setBannerDraft((prev) => (prev ? { ...prev, isActive: val } : prev))
                      }
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <p className="text-sm font-medium">Message</p>
                  <Textarea
                    value={bannerDraft.message}
                    onChange={(e) =>
                      setBannerDraft((prev) => (prev ? { ...prev, message: e.target.value } : prev))
                    }
                    placeholder="Banner message shown to users…"
                    className="text-sm resize-none"
                    rows={3}
                    autoFocus
                  />
                </div>

                <div className="space-y-1.5">
                  <p className="text-sm font-medium">Type</p>
                  <select
                    value={bannerDraft.bannerType}
                    onChange={(e) =>
                      setBannerDraft((prev) =>
                        prev ? { ...prev, bannerType: e.target.value as 'warning' | 'info' } : prev,
                      )
                    }
                    className="text-sm border border-border rounded-md px-3 py-1.5 bg-background w-full"
                  >
                    <option value="warning">⚠️ Warning</option>
                    <option value="info">ℹ️ Info</option>
                  </select>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setBannerDialogScreen(null)
                  setBannerDraft(null)
                }}
                disabled={bannerSaving}
              >
                Cancel
              </Button>
              <Button
                disabled={bannerSaving || !bannerDraft}
                onClick={async () => {
                  if (!bannerDraft || !bannerDialogScreen) return
                  const label =
                    BANNER_SCREENS.find((s) => s.key === bannerDialogScreen)?.label ??
                    bannerDialogScreen
                  setBannerSaving(true)
                  try {
                    const r = await fetch('/api/admin/banner', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        callerEmail: userEmail,
                        screen: bannerDialogScreen,
                        isActive: bannerDraft.isActive,
                        message: bannerDraft.message,
                        bannerType: bannerDraft.bannerType,
                      }),
                    })
                    if (r.ok) {
                      setBanners((prev) =>
                        prev.find((b) => b.screen === bannerDialogScreen)
                          ? prev.map((b) => (b.screen === bannerDialogScreen ? bannerDraft : b))
                          : [...prev, bannerDraft],
                      )
                      setBannerDialogScreen(null)
                      setBannerDraft(null)
                      toast({ title: `${label} banner saved` })
                    } else {
                      toast({ title: 'Save failed', variant: 'destructive' })
                    }
                  } catch {
                    toast({ title: 'Save failed', variant: 'destructive' })
                  } finally {
                    setBannerSaving(false)
                  }
                }}
              >
                {bannerSaving ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </TabsContent>
    </Tabs>
  )
}
